import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { StickyNote, Pin, PinOff, Pencil, Trash2, Plus, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ShareWhatsappButton from './ShareWhatsappButton';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_STYLES = {
  yellow: 'bg-amber-100 dark:bg-amber-500/15 border-[#D97706]/40',
  green: 'bg-emerald-100 dark:bg-emerald-500/15 border-[#4CAF50]/40',
  red: 'bg-red-50 border-red-400/50',
};
const COLOR_LABELS = { yellow: 'Amarillo', green: 'Verde', red: 'Rojo (Urgente)' };

const emptyDraft = { title: '', message: '', color: 'yellow', pinned: false };

export default function AnnouncementsView() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/announcements`, { withCredentials: true });
      setItems(data);
    } catch {
      toast.error('Error al cargar los avisos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(true);
  };
  const openEdit = (item) => {
    setDraft({ title: item.title, message: item.message, color: item.color, pinned: item.pinned });
    setEditingId(item.id);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const save = async (e) => {
    e.preventDefault();
    if (draft.title.trim().length < 2) {
      toast.error('El título debe tener al menos 2 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/api/announcements/${editingId}`, draft, { withCredentials: true });
        toast.success('Aviso actualizado');
      } else {
        await axios.post(`${API}/api/announcements`, draft, { withCredentials: true });
        toast.success('Aviso publicado');
      }
      closeForm();
      load();
    } catch {
      toast.error('No pudimos guardar el aviso');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (item) => {
    try {
      await axios.patch(
        `${API}/api/announcements/${item.id}`,
        { pinned: !item.pinned },
        { withCredentials: true }
      );
      load();
    } catch {
      toast.error('No pudimos actualizar el aviso');
    }
  };

  const remove = async (item) => {
    if (!window.confirm('¿Eliminar este aviso?')) return;
    try {
      await axios.delete(`${API}/api/announcements/${item.id}`, { withCredentials: true });
      toast.success('Aviso eliminado');
      load();
    } catch {
      toast.error('No pudimos eliminar el aviso');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
            Muro de avisos
          </h1>
          <p className="text-muted-foreground">Recordá cosas rápidas para la familia</p>
        </div>
        <button
          data-testid="new-announcement-btn"
          onClick={openNew}
          className="px-5 py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all shadow-md hover:-translate-y-0.5 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuevo aviso
        </button>
      </div>

      {/* SOS quick access — vive únicamente dentro del Muro de Avisos */}
      <button
        data-testid="sos-btn"
        onClick={() => navigate('/dashboard/emergency')}
        className="w-full flex items-center gap-4 p-4 sm:p-5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
      >
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-7 h-7" strokeWidth={2} />
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-white/80">Emergencia</p>
          <p className="text-lg sm:text-xl font-bold" >
            Contactos rápidos SOS
          </p>
          <p className="text-xs text-white/80 hidden sm:block">Médicos, ambulancia, obra social y más</p>
        </div>
        <span className="text-sm font-semibold px-3 py-1.5 bg-white/20 rounded-full">Abrir →</span>
      </button>

      {showForm && (
        <form
          data-testid="announcement-form"
          onSubmit={save}
          className="bg-surface rounded-2xl shadow-lg p-6 sm:p-8 space-y-4 border-2 border-[#D97706]/30"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-foreground">{editingId ? 'Editar aviso' : 'Nuevo aviso'}</h2>
            <button type="button" onClick={closeForm} className="p-2 hover:bg-muted rounded-full text-muted-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div>
            <label className="block text-sm font-semibold text-muted-foreground mb-2">Título</label>
            <input
              data-testid="ann-title-input"
              type="text"
              required
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              placeholder="Ej: Reunión de padres"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-muted-foreground mb-2">Mensaje</label>
            <textarea
              data-testid="ann-message-input"
              required
              rows={3}
              value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706] resize-none"
              placeholder="Escribe el aviso..."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Color</label>
              <div className="flex gap-2">
                {['yellow', 'green', 'red'].map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`ann-color-${c}`}
                    onClick={() => setDraft({ ...draft, color: c })}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border-2 ${COLOR_STYLES[c]} ${
                      draft.color === c ? 'ring-2 ring-[#1E3A5F]' : 'opacity-70'
                    }`}
                  >
                    {COLOR_LABELS[c]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="ann-pinned-toggle"
                  type="checkbox"
                  checked={draft.pinned}
                  onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
                  className="w-5 h-5 accent-[#D97706]"
                />
                <span className="text-sm font-semibold text-muted-foreground">Fijar arriba</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeForm} className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-full">Cancelar</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="ann-save-btn"
              className="px-6 py-2 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] transition-all disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Publicar'}
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="bg-surface rounded-2xl shadow-md p-10 text-center">
          <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground">Todavía no publicaste avisos</p>
          <p className="text-muted-foreground text-sm mt-1">Empezá creando el primero con el botón de arriba</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`announcement-${item.id}`}
              className={`relative rounded-2xl border-2 p-5 shadow-sm ${COLOR_STYLES[item.color] || COLOR_STYLES.yellow}`}
            >
              {item.pinned && (
                <div className="absolute -top-2 -right-2 bg-[#1E3A5F] text-white px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow">
                  <Pin className="w-3 h-3" /> Fijado
                </div>
              )}
              <h3 className="font-bold text-foreground text-lg pr-6">{item.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{item.message}</p>
              <p className="text-[10px] text-muted-foreground mt-3">
                {item.author_name || 'Sin autor'} · {new Date(item.created_at).toLocaleString('es-AR')}
              </p>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border/60">
                <button
                  onClick={() => togglePin(item)}
                  data-testid={`ann-pin-${item.id}`}
                  title={item.pinned ? 'Desfijar' : 'Fijar arriba'}
                  className="p-2 hover:bg-white/60 rounded-full text-foreground"
                >
                  {item.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => openEdit(item)}
                  data-testid={`ann-edit-${item.id}`}
                  title="Editar"
                  className="p-2 hover:bg-white/60 rounded-full text-foreground"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <ShareWhatsappButton
                  title={item.title}
                  description={item.message}
                  kind="reminder"
                  testId={`ann-share-${item.id}`}
                />
                <button
                  onClick={() => remove(item)}
                  data-testid={`ann-delete-${item.id}`}
                  title="Eliminar"
                  className="p-2 hover:bg-red-50 rounded-full text-red-600 ml-auto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
